import type {RepositoryNWO, Repository} from '@github-ui/current-repository'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {Type} from '@github-ui/react-query-persister/typebox'
import {oneMonthAgo, oneYearAgo, threeMonthsAgo} from './utils/age-qualifier-dates'

export const DashboardIssueSchema = Type.Object({
  author: Type.String(),
  id: Type.Number(),
  title: Type.String(),
  number: Type.Number(),
  permalink: Type.String(),
  commentCount: Type.Number(),
  createdAt: Type.String(),
  updatedAt: Type.String(),
  repository: Type.Object({
    id: Type.Number(),
    isPrivate: Type.Boolean(),
    name: Type.String(),
    ownerLogin: Type.String(),
    copilotAgentEnabled: Type.Boolean(),
  }),
  readByCurrentUser: Type.Boolean(),
  assignedToCopilot: Type.Boolean(),
})

export type DashboardIssue = Type.Static<typeof DashboardIssueSchema>

export const DashboardIssueValidator = Compile(Type.Array(DashboardIssueSchema))

export const DashboardPullRequestSchema = Type.Object({
  author: Type.String(),
  id: Type.Number(),
  title: Type.String(),
  number: Type.Number(),
  permalink: Type.String(),
  commentCount: Type.Number(),
  updatedAt: Type.String(),
  createdAt: Type.String(),
  suggestedAction: Type.Union([Type.String(), Type.Null()]),
  headSha: Type.String(),
  repoNameWithOwner: Type.Any(),
  isDraft: Type.Boolean(),
  inMergeQueue: Type.Boolean(),
  readByCurrentUser: Type.Boolean(),
  repository: Type.Any(),
  assignedToCopilot: Type.Boolean(),
  viewerCanRequestCopilotCodeReview: Type.Boolean(),
})

export const DashboardPullRequestValidator = Compile(Type.Array(DashboardPullRequestSchema))

export type DashboardPullRequest = Type.Static<typeof DashboardPullRequestSchema> & {
  repoNameWithOwner: RepositoryNWO
  repository: Repository
}

export type MenuItem<TQualifier extends QueryQualifier | AgentQueryQualifier> = {
  label: string
  value: TQualifier
}

export type MaxAgeMenuItem<TQualifier extends MaxAgeQualifier> = {
  label: string
  value: TQualifier
}

export const QueryQualifier = {
  Authored: 'author',
  Mentions: 'mentions',
  ReviewRequested: 'review-requested',
  ReviewedBy: 'reviewed-by',
  Assigned: 'assignee',
  Involves: 'involves',
} as const

export const AgentQueryQualifier = {
  Open: 'open',
  Closed: 'closed',
} as const

export const MaxAgeQualifier = {
  OneMonth: 'one-month',
  ThreeMonths: 'three-months',
  OneYear: 'one-year',
  NoMax: 'no-max',
} as const

export type QueryQualifier = (typeof QueryQualifier)[keyof typeof QueryQualifier]
export type AgentQueryQualifier = (typeof AgentQueryQualifier)[keyof typeof AgentQueryQualifier]
export type MaxAgeQualifier = (typeof MaxAgeQualifier)[keyof typeof MaxAgeQualifier]

const maxAgeValueMap: Record<MaxAgeQualifier, (now: Date) => string> = {
  [MaxAgeQualifier.OneMonth]: now => oneMonthAgo(now),
  [MaxAgeQualifier.ThreeMonths]: now => threeMonthsAgo(now),
  [MaxAgeQualifier.OneYear]: now => oneYearAgo(now),
  [MaxAgeQualifier.NoMax]: () => '',
}

export const getMaxAgeValue = (qualifier: MaxAgeQualifier, now: Date = new Date()): string => {
  return maxAgeValueMap[qualifier](now)
}
